/* __next_internal_client_entry_do_not_use__ AppDownloadPromoBanner,DesktopAppDownloadPhoneInputForm auto */ import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { AppDownload } from "@faire/retailer-visitor-shared/components/MobileAppPromoBanner/AppDownload";
import { useAppDownloadState } from "@faire/retailer-visitor-shared/components/MobileAppPromoBanner/AppDownloadState";
import { LazyPhoneInput } from "@faire/retailer-visitor-shared/components/PhoneInput/LazyPhoneInput";
import { optimizeImageUrl } from "@faire/web--source/common/images/optimizeImages";
import { useStrictLocalization } from "@faire/web--source/common/localization/localize";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { Button } from "@faire/web--source/slate/Button";
import { Column, Flex, Row } from "@faire/web--source/slate/Layout";
import { LoadingSpinner } from "@faire/web--source/slate/LoadingSpinner";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { ResponsiveContentV2 } from "@faire/web--source/ui/ResponsiveContentV2";
import { WhenVisibilityIsImminent } from "@faire/web--source/ui/VisibilitySensor/WhenVisibilityIsImminent";
import { observer } from "mobx-react";
import React, { Suspense, useCallback, useMemo } from "react";
import styled from "styled-components";
var AppDownloadPromoBannerBase = function(props) {
    var eventPrefix = props.eventPrefix, header = props.header, body = props.body, onClick = props.onClick;
    var strictLocalize = useStrictLocalization().strictLocalize;
    var message = useMemo(function() {
        return strictLocalize({
            id: "8CJi4w",
            defaultMessage: [
                {
                    type: 0,
                    value: "Download the Faire app for a better shopping experience."
                }
            ]
        });
    }, [
        strictLocalize
    ]);
    var isSSR = useIsSSR();
    return /*#__PURE__*/ _jsxs(Wrapper, {
        children: [
            /*#__PURE__*/ _jsx(BannerImage, {
                $isSSR: isSSR
            }),
            /*#__PURE__*/ _jsxs(Content, {
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        variant: "pageHeaderSerifRegular",
                        children: header !== null && header !== void 0 ? header : /*#__PURE__*/ _jsx(StrictLocalMsg, {
                            id: "YdxU/V",
                            defaultMessage: [
                                {
                                    type: 0,
                                    value: "Wholesale whenever, wherever."
                                }
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsx(Spacer, {
                        height: "1x"
                    }),
                    /*#__PURE__*/ _jsx(Typography, {
                        children: body !== null && body !== void 0 ? body : message
                    }),
                    /*#__PURE__*/ _jsx(Spacer, {
                        height: {
                            mobileAndAbove: "2x",
                            tabletAndAbove: "3x"
                        }
                    }),
                    /*#__PURE__*/ _jsx(ResponsiveContentV2, {
                        tabletAndBelow: /*#__PURE__*/ _jsx(AppDownload, {
                            onClick: onClick,
                            eventPrefix: eventPrefix,
                            noModal: true
                        }),
                        desktopAndAbove: /*#__PURE__*/ _jsx(DesktopAppDownloadPhoneInputForm, {
                            onClick: onClick,
                            eventPrefix: eventPrefix
                        })
                    })
                ]
            })
        ]
    });
};
export var AppDownloadPromoBanner = observer(AppDownloadPromoBannerBase);
export var DesktopAppDownloadPhoneInputForm = function(props) {
    var eventPrefix = props.eventPrefix, onClick = props.onClick;
    var _useAppDownloadState = useAppDownloadState(), sendAppLink = _useAppDownloadState.sendAppLink, isInvalidPhoneNumber = _useAppDownloadState.isInvalidPhoneNumber, setPhoneNumber = _useAppDownloadState.setPhoneNumber, phoneNumber = _useAppDownloadState.phoneNumber;
    var strictLocalize = useStrictLocalization().strictLocalize;
    var onPhoneInputButtonClick = useCallback(function() {
        onClick === null || onClick === void 0 ? void 0 : onClick();
        sendAppLink(eventPrefix);
    }, [
        sendAppLink,
        onClick,
        eventPrefix
    ]);
    var getPhoneInputButton = function(disabled) {
        return /*#__PURE__*/ _jsx(PhoneInputButton, {
            disabled: disabled || false,
            onClick: onPhoneInputButtonClick,
            children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                id: "vzUnMe",
                defaultMessage: [
                    {
                        type: 0,
                        value: "Send Download Link"
                    }
                ]
            })
        });
    };
    var loadingFallback = /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Column, {
                align: "center",
                style: {
                    flexGrow: 1,
                    alignSelf: "center",
                    maxWidth: "300px"
                },
                children: /*#__PURE__*/ _jsx(LoadingSpinner, {
                    size: "medium"
                })
            }),
            /*#__PURE__*/ _jsx(Spacer, {
                width: "2x"
            }),
            getPhoneInputButton(true)
        ]
    });
    return /*#__PURE__*/ _jsx(Row, {
        align: "flex-start",
        children: /*#__PURE__*/ _jsx(WhenVisibilityIsImminent, {
            children: /*#__PURE__*/ _jsxs(Suspense, {
                fallback: loadingFallback,
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        style: {
                            flexGrow: 1,
                            maxWidth: "300px"
                        },
                        children: /*#__PURE__*/ _jsx(LazyPhoneInput, {
                            helper: strictLocalize({
                                id: "ZnfaeA",
                                defaultMessage: [
                                    {
                                        type: 0,
                                        value: "Standard messaging rates apply"
                                    }
                                ]
                            }),
                            error: isInvalidPhoneNumber ? strictLocalize({
                                id: "FPcbu0",
                                defaultMessage: [
                                    {
                                        type: 0,
                                        value: "Please enter a valid phone number"
                                    }
                                ]
                            }) : undefined,
                            onChangePhoneNumber: setPhoneNumber,
                            defaultPhoneNumber: phoneNumber
                        })
                    }),
                    /*#__PURE__*/ _jsx(Spacer, {
                        width: "2x"
                    }),
                    getPhoneInputButton()
                ]
            })
        })
    });
};
var Wrapper = styled(Flex).attrs({
    direction: {
        mobileAndAbove: "column",
        tabletAndAbove: "row-reverse"
    }
}).withConfig({
    componentId: "sc-f5d7dcff-0"
})([
    "background-color:#f4f4f0;"
]);
var Content = styled(Column).withConfig({
    componentId: "sc-f5d7dcff-1"
})([
    "width:100%;padding:",
    ";",
    "{padding:",
    ";}"
], getSpacing("3x"), responsiveMediaQueries.tabletAndAbove, getSpacing("5x"));
var PhoneInputButton = styled(Button).withConfig({
    componentId: "sc-f5d7dcff-2"
})([
    "height:40px;width:fit-content;min-width:160px;flex-shrink:0;"
]);
var getBackgroundUrl = function(param) {
    var $isSSR = param.$isSSR;
    return optimizeImageUrl({
        url: "https://cdn.faire.com/fastly/57014213f9cd3f0457e8d177e5f88506c1f3468db862b0f58f9a9344b3574078.png"
    }, $isSSR, {
        width: 600
    });
};
var BannerImage = styled.div.withConfig({
    componentId: "sc-f5d7dcff-3"
})([
    "flex-shrink:0;width:100%;height:172px;background-image:url(",
    ");background-size:cover;background-position:center;",
    "{background-position:left;}",
    "{width:50%;height:auto;}"
], getBackgroundUrl, responsiveMediaQueries.tablet, responsiveMediaQueries.tabletAndAbove);
